/**
 * Addons Service
 *
 * Type-safe addon management service using @monorepo/shared schemas
 * Addons are plans with category='addon'
 */

import {
  type EntityStatus,
  PLAN_CATEGORY,
} from '@monorepo/shared/schemas/common'
import {
  type CreatePlan,
  type CreatePlanAgentAction,
  CreatePlanAgentActionSchema,
  type CreatePlanCharge,
  CreatePlanChargeSchema,
  type CreatePlanFeature,
  CreatePlanFeatureSchema,
  CreatePlanSchema,
  type CreatePricingTier,
  CreatePricingTierSchema,
  type PaginatedPlansResponse,
  PaginatedPlansResponseSchema,
  type Plan,
  type PlanAgentAction,
  PlanAgentActionSchema,
  type PlanCharge,
  PlanChargeSchema,
  type PlanFeature,
  PlanFeatureSchema,
  type PlanQuery,
  type PlanResponse,
  PlanResponseSchema,
  type PricingTier,
  PricingTierSchema,
  type UpdatePlan,
  type UpdatePlanAgentAction,
  UpdatePlanAgentActionSchema,
  type UpdatePlanCharge,
  UpdatePlanChargeSchema,
  type UpdatePlanFeature,
  UpdatePlanFeatureSchema,
  UpdatePlanSchema,
  type UpdatePricingTier,
  UpdatePricingTierSchema,
} from '@monorepo/shared/schemas/plans'
import { del, get, patch, post, put } from '@/lib/api-client'

export const addonService = {
  /**
   * List all addons (filtered by query)
   */
  async getAddons(query?: PlanQuery): Promise<PaginatedPlansResponse> {
    const queryParams = new URLSearchParams()

    // Always filter by addon category
    queryParams.append('category', PLAN_CATEGORY.addon)

    if (query?.plan_type) queryParams.append('plan_type', query.plan_type)
    if (query?.status) queryParams.append('status', query.status)
    if (query?.target_audience)
      queryParams.append('target_audience', query.target_audience)
    if (query?.billing_period)
      queryParams.append('billing_period', query.billing_period)
    if (query?.currency) queryParams.append('currency', query.currency)
    if (query?.search) queryParams.append('search', query.search)
    if (query?.code) queryParams.append('code', query.code)
    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())

    const url = `/plans${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedPlansResponseSchema)
  },

  /**
   * Get addon by ID (includes charges, agent_actions, features)
   */
  async getAddon(id: string): Promise<PlanResponse> {
    return get(`/plans/${id}`, PlanResponseSchema)
  },

  /**
   * Create new addon
   */
  async createAddon(data: CreatePlan): Promise<PlanResponse> {
    // Ensure category is set to addon
    const addonData = {
      ...data,
      category: PLAN_CATEGORY.addon,
    }
    const validated = CreatePlanSchema.parse(addonData)

    return post('/plans', validated, PlanResponseSchema)
  },

  /**
   * Update addon
   */
  async updateAddon(id: string, data: UpdatePlan): Promise<PlanResponse> {
    const validated = UpdatePlanSchema.parse(data)

    return patch(`/plans/${id}`, validated, PlanResponseSchema)
  },

  /**
   * Delete addon (soft delete)
   */
  async deleteAddon(id: string): Promise<void> {
    await del(`/plans/${id}`)
  },

  /**
   * Update addon status
   */
  async updateStatus(id: string, status: EntityStatus): Promise<PlanResponse> {
    const validated = UpdatePlanSchema.parse({ status })

    return patch(`/plans/${id}`, validated, PlanResponseSchema)
  },

  /**
   * Clone addon (creates copy with new code)
   */
  async cloneAddon(
    id: string,
    code: string,
    name?: string
  ): Promise<PlanResponse> {
    return post(`/plans/${id}/clone`, { code, name }, PlanResponseSchema)
  },

  /**
   * Check if addon code is available/unique
   */
  async checkCodeAvailable(code: string): Promise<boolean> {
    const response = (await get(
      `/plans/check-code/${encodeURIComponent(code)}`
    )) as { available?: boolean }
    return response.available || false
  },

  // ============================================================================
  // ADDON CHARGES
  // ============================================================================

  /**
   * Add charge to addon
   */
  async addCharge(
    addonId: string,
    data: CreatePlanCharge
  ): Promise<PlanCharge> {
    const validated = CreatePlanChargeSchema.parse(data)

    return post(`/plans/${addonId}/charges`, validated, PlanChargeSchema)
  },

  /**
   * Update charge
   */
  async updateCharge(
    addonId: string,
    chargeId: string,
    data: UpdatePlanCharge
  ): Promise<PlanCharge> {
    const validated = UpdatePlanChargeSchema.parse(data)

    return patch(
      `/plans/${addonId}/charges/${chargeId}`,
      validated,
      PlanChargeSchema
    )
  },

  /**
   * Remove charge from addon
   */
  async removeCharge(addonId: string, chargeId: string): Promise<void> {
    await del(`/plans/${addonId}/charges/${chargeId}`)
  },

  // ============================================================================
  // ADDON FEATURES
  // ============================================================================

  /**
   * Add feature to addon
   */
  async addFeature(
    addonId: string,
    data: CreatePlanFeature
  ): Promise<PlanFeature> {
    const validated = CreatePlanFeatureSchema.parse(data)

    return post(`/plans/${addonId}/features`, validated, PlanFeatureSchema)
  },

  /**
   * Update addon feature
   */
  async updateFeature(
    addonId: string,
    featureId: string,
    data: UpdatePlanFeature
  ): Promise<PlanFeature> {
    const validated = UpdatePlanFeatureSchema.parse(data)

    return put(
      `/plans/${addonId}/features/${featureId}`,
      validated,
      PlanFeatureSchema
    )
  },

  /**
   * Remove feature from addon
   */
  async removeFeature(addonId: string, featureId: string): Promise<void> {
    await del(`/plans/${addonId}/features/${featureId}`)
  },

  /**
   * Add pricing tier to addon feature
   */
  async addFeatureTier(
    addonId: string,
    featureId: string,
    data: CreatePricingTier
  ): Promise<PricingTier> {
    const validated = CreatePricingTierSchema.parse(data)

    return post(
      `/plans/${addonId}/features/${featureId}/tiers`,
      validated,
      PricingTierSchema
    )
  },

  /**
   * Update pricing tier for addon feature
   */
  async updateFeatureTier(
    addonId: string,
    featureId: string,
    tierId: string,
    data: UpdatePricingTier
  ): Promise<PricingTier> {
    const validated = UpdatePricingTierSchema.parse(data)

    return put(
      `/plans/${addonId}/features/${featureId}/tiers/${tierId}`,
      validated,
      PricingTierSchema
    )
  },

  /**
   * Remove pricing tier from addon feature
   */
  async removeFeatureTier(
    addonId: string,
    featureId: string,
    tierId: string
  ): Promise<void> {
    await del(`/plans/${addonId}/features/${featureId}/tiers/${tierId}`)
  },

  // ============================================================================
  // ADDON AGENT ACTIONS
  // ============================================================================

  /**
   * Add agent action to addon
   */
  async addAgentAction(
    addonId: string,
    data: CreatePlanAgentAction
  ): Promise<PlanAgentAction> {
    const validated = CreatePlanAgentActionSchema.parse(data)

    return post(
      `/plans/${addonId}/agent-actions`,
      validated,
      PlanAgentActionSchema
    )
  },

  /**
   * Update addon agent action
   */
  async updateAgentAction(
    addonId: string,
    agentActionId: string,
    data: UpdatePlanAgentAction
  ): Promise<PlanAgentAction> {
    const validated = UpdatePlanAgentActionSchema.parse(data)

    return put(
      `/plans/${addonId}/agent-actions/${agentActionId}`,
      validated,
      PlanAgentActionSchema
    )
  },

  /**
   * Remove agent action from addon
   */
  async removeAgentAction(
    addonId: string,
    agentActionId: string
  ): Promise<void> {
    await del(`/plans/${addonId}/agent-actions/${agentActionId}`)
  },

  /**
   * Add pricing tier to addon agent action
   */
  async addAgentActionTier(
    addonId: string,
    agentActionId: string,
    data: CreatePricingTier
  ): Promise<PricingTier> {
    const validated = CreatePricingTierSchema.parse(data)

    return post(
      `/plans/${addonId}/agent-actions/${agentActionId}/tiers`,
      validated,
      PricingTierSchema
    )
  },

  /**
   * Update pricing tier for addon agent action
   */
  async updateAgentActionTier(
    addonId: string,
    agentActionId: string,
    tierId: string,
    data: UpdatePricingTier
  ): Promise<PricingTier> {
    const validated = UpdatePricingTierSchema.parse(data)

    return put(
      `/plans/${addonId}/agent-actions/${agentActionId}/tiers/${tierId}`,
      validated,
      PricingTierSchema
    )
  },

  /**
   * Remove pricing tier from addon agent action
   */
  async removeAgentActionTier(
    addonId: string,
    agentActionId: string,
    tierId: string
  ): Promise<void> {
    await del(
      `/plans/${addonId}/agent-actions/${agentActionId}/tiers/${tierId}`
    )
  },
}

// Re-export types for convenience
export type {
  Plan,
  CreatePlan,
  UpdatePlan,
  PlanResponse,
  PaginatedPlansResponse,
  PlanQuery,
  CreatePlanCharge,
  UpdatePlanCharge,
  PlanCharge,
  CreatePlanFeature,
  UpdatePlanFeature,
  PlanFeature,
  CreatePlanAgentAction,
  UpdatePlanAgentAction,
  PlanAgentAction,
  CreatePricingTier,
  UpdatePricingTier,
  PricingTier,
}

// Default export for backward compatibility
export default addonService
